import axios from 'axios'
import {
  ORIGIN_STAMP, ORIGIN_EDITED, ORIGIN_ROSTER,
  stampEventsByDate, stampOriginsOfMonth, rosterEnteredDayIndexes,
  mapUrlOf, clockGapMinutes, isStampEvent, normalizedClock
} from '../utils/stampOrigin'
import { stampPlaceName } from '../utils/stampPlaceName'
import placeTexts from '../setting/stampingPlaceTexts'

/**
 * 勤務表の「入力区分」（打刻・打刻後修正・手入力）と、打刻履歴。
 *
 * 勤務表（Roster.vue）へ混ぜて使う。本人の勤務表も、承認者・管理者が開く勤務表
 * （fromExternal）も、同じ判定（utils/stampOrigin.js）を通す。
 *
 * **読めなくても勤務表は動かす。**打刻の記録が取れないときは、印も色も出さないだけにする
 * （記録の口が落ちたせいで勤務表が開けない・保存できない、を作らない）。
 */

const KIND_LABELS = {
  START: '出勤',
  END: '退勤',
  BREAK_START: '休憩開始',
  BREAK_END: '休憩終了'
}

const LOCATION_LABELS = {
  OFF: '記録オフ',
  DENIED: '未許可',
  UNAVAILABLE: '取得失敗',
  TIMEOUT: 'タイムアウト',
  UNSUPPORTED: '非対応'
}

const ORIGIN_LABELS = {
  [ORIGIN_STAMP]: '打刻',
  [ORIGIN_EDITED]: '打刻後修正',
  [ORIGIN_ROSTER]: '手入力'
}

export default {
  data () {
    return {
      stampEvents: [],
      stampSince: '',
      stampLocationEnabled: false,
      // 記録を読んだ月（「2026-10」の形）。表示中の月と違えば、印を出さない。
      stampEventsMonth: '',
      stampOpenIndex: -1,
      // 読み込んだ（保存されている）開始・終了。いまの値がこれと違う欄は、いま書き換えている欄。
      stampSavedStart: [],
      stampSavedEnd: [],
      stampLoadGeneration: 0,
      // 打刻した場所の住所（「緯度,経度」→ 住所。変換できなかった場所は空文字。まだ調べていない場所は鍵が無い）。
      stampPlaceNames: {}
    }
  },
  computed: {
    stampShownMonthKey () {
      if (!this.selectYear || !this.selectMonth) return ''
      return this.selectYear + '-' + String(this.selectMonth).padStart(2, '0')
    },
    stampDates () {
      const key = this.stampShownMonthKey
      if (!key) return []
      return (this.monthDay || []).map((_, index) =>
        key + '-' + String(index + 1).padStart(2, '0'))
    },
    stampEventsOfShownMonth () {
      return this.stampEventsMonth === this.stampShownMonthKey ? this.stampEvents : []
    },
    stampByDate () {
      return stampEventsByDate(this.stampEventsOfShownMonth)
    },
    /** 行ごとの入力区分。出さない日は null。 */
    stampOrigins () {
      if (this.stampEventsMonth !== this.stampShownMonthKey) return []
      return stampOriginsOfMonth({
        dates: this.stampDates,
        events: this.stampEventsOfShownMonth,
        starts: this.start,
        ends: this.end,
        savedStarts: this.stampSavedStart,
        savedEnds: this.stampSavedEnd
      })
    },
    /** この会社で入力区分を出すか（打刻の記録が1件でもあるか）。 */
    stampOriginAvailable () {
      return !!this.stampSince && this.stampEventsMonth === this.stampShownMonthKey
    },
    stampRosterEnteredIndexes () {
      return rosterEnteredDayIndexes(this.stampOrigins)
    },
    /** 勤務表の上に出す1行。出さないときは空文字。 */
    stampSummaryText () {
      if (!this.stampOriginAvailable) return ''
      if (!this.stampOrigins.some(origin => !!origin)) return ''
      const indexes = this.stampRosterEnteredIndexes
      if (!indexes.length) return 'すべて打刻通りです'
      const days = indexes.map(index => Number(this.selectMonth) + '/' + (index + 1))
      return '手入力・修正あり：' + indexes.length + '日（' + days.join('・') + '）'
    }
  },
  methods: {
    /** 表示中の月の、打刻の記録を読む。失敗したら印を出さないだけにする。 */
    loadStampEvents (year, month) {
      const generation = ++this.stampLoadGeneration
      const key = year + '-' + String(month).padStart(2, '0')
      this.stampOpenIndex = -1
      const request = this.fromExternal
        ? axios.get(process.env.VUE_APP_API_ORIGIN + '/admin/roster/stampingEvents', {
          params: { user_id: this.user_id, year, month }
        })
        : axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/events', {
          params: { year, month }
        })
      return request
        .then(response => {
          if (generation !== this.stampLoadGeneration) return
          const data = (response && response.data) || {}
          this.stampEvents = Array.isArray(data.events) ? data.events : []
          this.stampSince = typeof data.since === 'string' ? data.since : ''
          this.stampLocationEnabled = data.location_enabled === true
          this.stampEventsMonth = key
        })
        .catch(() => {
          if (generation !== this.stampLoadGeneration) return
          this.stampEvents = []
          this.stampSince = ''
          this.stampLocationEnabled = false
          this.stampEventsMonth = ''
        })
    },
    stampOriginAt (index) {
      return this.stampOrigins[index] || null
    },
    stampOriginLabelAt (index) {
      const origin = this.stampOriginAt(index)
      return origin ? ORIGIN_LABELS[origin.origin] : ''
    },
    /** 開始時刻の欄を濃くするか（勤務表で入力・修正した時刻か）。 */
    stampStartByRosterAt (index) {
      const origin = this.stampOriginAt(index)
      return !!origin && origin.startByRoster && !!this.start[index]
    },
    stampEndByRosterAt (index) {
      const origin = this.stampOriginAt(index)
      return !!origin && origin.endByRoster && !!this.end[index]
    },
    toggleStampHistory (index) {
      this.stampOpenIndex = this.stampOpenIndex === index ? -1 : index
      if (this.stampOpenIndex === index) this.loadStampPlaceNames(index)
    },
    stampPlaceKey (event) {
      return event.latitude + ',' + event.longitude
    },
    /** 開いた日の、場所が記録されている打刻の住所を調べる（開いたときにだけ調べる）。 */
    loadStampPlaceNames (index) {
      this.stampEventsAt(index)
        .filter(event => event.location_status === 'OK')
        .forEach(event => {
          const key = this.stampPlaceKey(event)
          if (Object.prototype.hasOwnProperty.call(this.stampPlaceNames, key)) return
          stampPlaceName(event.latitude, event.longitude)
            .then(name => { this.$set(this.stampPlaceNames, key, name || '') })
        })
    },
    stampEventsAt (index) {
      const day = this.stampByDate[this.stampDates[index]]
      return day ? day.events : []
    },
    /** 読み込んだ開始・終了を控える（勤務表が行を読み込んだところで呼ぶ）。 */
    captureStampSavedTime (index, start, end) {
      this.stampSavedStart.splice(index, 1, start || '')
      this.stampSavedEnd.splice(index, 1, end || '')
    },
    stampKindLabel (event) {
      if (event.event_kind === 'ROSTER_START' || event.event_kind === 'ROSTER_END') {
        const field = event.event_kind === 'ROSTER_START' ? '開始' : '終了'
        const time = normalizedClock(event.stamped_time)
        return time ? field + '変更（' + time + '）' : field + '削除'
      }
      return KIND_LABELS[event.event_kind] || ''
    },
    /** 打刻時刻（勤務日と違う日に打ったものは、日付も付ける）。 */
    stampEventTime (event) {
      const at = String(event.event_at || '')
      if (at.length < 16) return ''
      const date = at.slice(0, 10)
      const time = at.slice(11, 16)
      return date === event.work_date
        ? time
        : Number(date.slice(5, 7)) + '/' + Number(date.slice(8, 10)) + ' ' + time
    },
    stampReceivedTime (event) {
      const at = String(event.received_at || '')
      if (at.length < 16) return ''
      const eventDate = String(event.event_at || '').slice(0, 10)
      const date = at.slice(0, 10)
      const time = at.slice(11, 16)
      return date === eventDate
        ? time
        : Number(date.slice(5, 7)) + '/' + Number(date.slice(8, 10)) + ' ' + time
    },
    /** 打刻時刻と受信時刻が5分以上ずれているか（目立たせる）。 */
    stampClockGapIsLarge (event) {
      // 勤務表で変えた記録は、端末の時計を使わない（比べるものが無い）。
      if (!isStampEvent(event)) return false
      const gap = clockGapMinutes(event)
      return gap != null && Math.abs(gap) >= 5
    },
    stampLocationText (event) {
      // 勤務表で変えた記録に、場所は無い（「記録オフ」と出すと、打刻の場所の話に見える）。
      if (!isStampEvent(event)) return ''
      if (event.location_status !== 'OK') {
        return LOCATION_LABELS[event.location_status] || ''
      }
      // 緯度・経度の数字は、見ても場所が分からない。住所（町丁目まで）で出す。
      const key = this.stampPlaceKey(event)
      if (!Object.prototype.hasOwnProperty.call(this.stampPlaceNames, key)) return '住所を取得中...'
      const name = this.stampPlaceNames[key]
      if (!name) return this.stampPlacePrefix(event) + '位置情報のみ記録（住所変換不可）'
      const address = event.accuracy_m == null
        ? name + ' 付近'
        : name + ' 付近（誤差：約' + event.accuracy_m + 'm）'
      return this.stampPlacePrefix(event) + address
    },
    /** 登録した場所の範囲内だった打刻は、場所の名前を住所の前に出す（「本社：…」）。 */
    stampPlacePrefix (event) {
      return event.place_in_range === true && event.place_name ? event.place_name + '：' : ''
    },
    /** 登録した場所の範囲外だった打刻の印（範囲内・判定なしは空文字）。 */
    stampOutOfRangeText (event) {
      if (event.location_status !== 'OK' || event.place_in_range !== false) return ''
      return event.place_distance_m == null
        ? placeTexts.outOfRange
        : placeTexts.outOfRangeWithDistance.replace('{n}', this.stampDistanceText(event.place_distance_m))
    },
    /** 距離の書き方（1km 未満は m、それ以上は km を小数1桁）。 */
    stampDistanceText (meters) {
      const value = Number(meters)
      return value < 1000 ? value + 'm' : (value / 1000).toFixed(1) + 'km'
    },
    /** 緯度・経度の数字（欄にカーソルを置くと出る。住所で足りないときの控え）。 */
    stampCoordinatesText (event) {
      if (event.location_status !== 'OK') return ''
      return Number(event.latitude).toFixed(6) + ', ' + Number(event.longitude).toFixed(6)
    },
    stampMapUrl (event) {
      return mapUrlOf(event)
    }
  }
}
